import { InputRandom, InputSelect } from "@components-common/forms";
import { useField } from "formik";
import { FC, useEffect } from "react";
import { DropdownOption } from "../../wizard.types";
import { Table } from "taltech-styleguide";

export type RowElementType =
  | "wallInterior"
  | "wallExterior"
  | "floor"
  | "floorGround"
  | "roof"
  | "windowType";

export type Props = {
  rows: string[];
  selectedType: string;
  dropdownOptions: {
    wallInterior: any[];
    wallExterior: any[];
    floor: any[];
    floorGround: any[];
    roof: any[];
    windowType: any[];
  };
  setSelectedType: (val: string) => void;
};

/**
 * Function: splitAndCapitalize
 *
 * This function splits a camelCase string into words and capitalizes the first letter of the first word.
 *
 * @param {string} str - The string to split and capitalize.
 * @returns {string} The formatted string with spaces and capitalization.
 */
export const splitAndCapitalize = (str: string) => {
  const words = str.split(/(?=[A-Z])/);
  words[0] = words[0].charAt(0).toUpperCase() + words[0].slice(1);

  return words.join(" ");
};

/**
 * Functional Component: BuildingComponents
 *
 * This component renders a table of building components with dropdowns for selecting types.
 *
 * Props:
 * @param {Props} props - The properties passed to the component.
 * @param {string[]} props.rows - An array of row identifiers.
 * @param {string} props.selectedType - The currently selected type.
 * @param {Function} props.setSelectedType - Function to set the selected type.
 * @param {Object} props.dropdownOptions - An object containing dropdown options for each row type.
 *
 * Returns:
 * @returns {JSX.Element} A JSX element containing the table of building components.
 */
export const BuildingComponents: FC<Props> = ({
  rows,
  selectedType,
  setSelectedType,
  dropdownOptions,
}) => {
  return (
    <div className="w-100 overflow-auto">
      <Table>
        <thead>
          <tr>
            <td>Components</td>
            <td>Type</td>
          </tr>
        </thead>
        <tbody>
          {rows.map((row: string) => {
            if (!dropdownOptions[row as RowElementType])
              return (
                <TableRow
                  key={row}
                  row={row}
                  selectedType={selectedType}
                  setSelectedType={setSelectedType}
                  dropdownOptions={[]}
                />
              );
            else
              return (
                <TableRow
                  key={row}
                  row={row}
                  selectedType={selectedType}
                  setSelectedType={setSelectedType}
                  dropdownOptions={dropdownOptions[row as RowElementType].map(
                    (option: any) => {
                      return { value: option.id, text: option.name };
                    }
                  )}
                />
              );
          })}
        </tbody>
      </Table>
    </div>
  );
};

/**
 * Functional Component: TableRow
 *
 * This component renders a row in the building components table.
 *
 * Props:
 * @param {Object} props - The properties passed to the component.
 * @param {string} props.row - The identifier for the row.
 * @param {string} props.selectedType - The currently selected type.
 * @param {Function} props.setSelectedType - Function to set the selected type.
 * @param {Array} props.dropdownOptions - The dropdown options for the row.
 *
 * Returns:
 * @returns {JSX.Element} A JSX element containing a table row.
 */
const TableRow = ({
  row,
  selectedType,
  setSelectedType,
  dropdownOptions,
}: any) => {
  const [fieldCreationType, helpersCreationType, metaCreationType] = useField(
    `${row}.creationType`
  );
  const [fieldGenType, helpersGenType, metaGenType] = useField(
    `${row}.generatorType`
  );
  const [fieldOptionsElements, helpersOptionsElements, metaOptionsElements] =
    useField(`${row}.options.elements`);

  return (
    <tr className={`${selectedType === row && "bg-light-secondary"}`}>
      <td
        className={`${
          selectedType === row && "fw-bold text-secondary cursor-pointer"
        } w-10 py-1`}
        onClick={() => {
          if (fieldCreationType.value === "RANDOM") setSelectedType(row);
        }}
      >
        {splitAndCapitalize(row)} <text style={{ color: "red" }}> *</text>
      </td>
      <td className="py-1">
        <InputSelect
          id={fieldOptionsElements.name}
          showLabel={false}
          disabled={fieldCreationType.value === "RANDOM"}
          options={dropdownOptions}
        />
      </td>
      <td className="w-5 ps-3 py-1">
        <InputRandom
          name={`${fieldCreationType.name}`}
          value={`${fieldCreationType.value}`}
          changeFn={() => {
            if (!(fieldCreationType.value === "RANDOM")) {
              metaOptionsElements.setValue(
                dropdownOptions.map((option: DropdownOption) => option.value)
              );
              metaGenType.setValue("LIST");
              setSelectedType(row);
            } else {
              metaOptionsElements.setValue(dropdownOptions[0].value);
              metaGenType.setValue("NUMBER");
              setSelectedType("");
            }
          }}
          disabled={dropdownOptions.length === 0}
        />
      </td>
    </tr>
  );
};
